import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, X, ArrowUp } from 'lucide-react';
import LearnPage from './LearnPage';
import LearnSidebar from './LearnSidebar';
import { SECTIONS, SHARED_SECTIONS, NON_DIV_SECTIONS } from './learnSections';

export default function LearnIndex() {
  // Round 92g (Feb 2026) — when arriving from a Learn-guide breadcrumb
  // (e.g. /learn#metrics), React Router's client-side nav doesn't
  // auto-scroll to the hash anchor. Manually find the matching
  // section id on mount + hash change and scroll it into view. The
  // sections already carry `scroll-mt-24` so the sticky section nav
  // doesn't cover the heading.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || hash.length < 2) return;
    const id = hash.slice(1);
    // requestAnimationFrame ensures the section list has painted before
    // we attempt to scroll (LearnIndex renders all 7 sections eagerly,
    // but the layout pass still needs one tick).
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [hash]);

  // Round 92i (Feb 2026) — fuzzy search filter. When the user types
  // in the search box, filter cards to those where the title, blurb,
  // OR tag substring-match the query (case-insensitive). Empty query
  // = show everything. Sections with zero matches hide entirely. We
  // memoize the filtered SECTIONS list so we don't re-walk the array
  // on every keystroke + re-render.
  const [query, setQuery] = useState('');
  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS
      .map((s) => ({
        ...s,
        posts: s.posts.filter((p) => {
          const haystack = `${p.title} ${p.blurb} ${p.tag}`.toLowerCase();
          return haystack.includes(q);
        }),
      }))
      .filter((s) => s.posts.length > 0);
  }, [query]);
  // Round 97j (Feb 28, 2026) — apply the same search filter to the
  // non-dividend box so a "buyback" search collapses everything that
  // doesn't match, not just the dividend half of the page.
  const filteredNonDivSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NON_DIV_SECTIONS;
    return NON_DIV_SECTIONS
      .map((s) => ({
        ...s,
        posts: s.posts.filter((p) => {
          const haystack = `${p.title} ${p.blurb} ${p.tag}`.toLowerCase();
          return haystack.includes(q);
        }),
      }))
      .filter((s) => s.posts.length > 0);
  }, [query]);
  const filteredSharedSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SHARED_SECTIONS;
    return SHARED_SECTIONS
      .map((s) => ({
        ...s,
        posts: s.posts.filter((p) => {
          const haystack = `${p.title} ${p.blurb} ${p.tag}`.toLowerCase();
          return haystack.includes(q);
        }),
      }))
      .filter((s) => s.posts.length > 0);
  }, [query]);
  const totalMatched = filteredSections.reduce((sum, s) => sum + s.posts.length, 0)
    + filteredSharedSections.reduce((sum, s) => sum + s.posts.length, 0)
    + filteredNonDivSections.reduce((sum, s) => sum + s.posts.length, 0);
  const totalAll = SECTIONS.reduce((sum, s) => sum + s.posts.length, 0)
    + SHARED_SECTIONS.reduce((sum, s) => sum + s.posts.length, 0)
    + NON_DIV_SECTIONS.reduce((sum, s) => sum + s.posts.length, 0);

  // Round 96w (Jul 4 2026) — Scroll-driven active-section highlighting
  // (replaces the earlier IntersectionObserver approach which held the
  // last active id when the reader scrolled back above all sections).
  // Rule: active section = the LAST section whose top edge has crossed
  // above the observation line (viewport top + 15%). When the reader
  // is above every section (scrolled to the very top), we default to
  // the first section so a chip is always highlighted.
  const [activeSection, setActiveSection] = useState(SECTIONS[0]?.id || null);
  useEffect(() => {
    const allIds = [
      ...SECTIONS.map((s) => s.id),
      ...SHARED_SECTIONS.map((s) => s.id),
      ...NON_DIV_SECTIONS.map((s) => s.id),
    ];
    const compute = () => {
      const observationTop = window.innerHeight * 0.15;
      let active = allIds[0];
      for (const id of allIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= observationTop) {
          active = id;
        } else {
          break;
        }
      }
      setActiveSection(active);
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, []);

  const chipClass = (id, palette) => {
    const isActive = activeSection === id;
    if (palette === 'amber') {
      return isActive
        ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/70 bg-amber-500/25 dark:bg-amber-500/20 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.4)] text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-200 transition-colors whitespace-nowrap'
        : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card hover:border-amber-400/60 hover:bg-amber-500/10 dark:hover:border-amber-400/40 text-xs sm:text-sm font-medium text-foreground transition-colors whitespace-nowrap';
    }
    if (palette === 'sky') {
      return isActive
        ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-500/70 bg-sky-500/25 dark:bg-sky-500/20 shadow-[inset_0_0_0_1px_rgba(14,165,233,0.4)] text-xs sm:text-sm font-semibold text-sky-800 dark:text-sky-200 transition-colors whitespace-nowrap'
        : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card hover:border-sky-400/60 hover:bg-sky-500/10 dark:hover:border-sky-400/40 text-xs sm:text-sm font-medium text-foreground transition-colors whitespace-nowrap';
    }
    return isActive
      ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-violet-500/70 bg-violet-500/25 dark:bg-violet-500/20 shadow-[inset_0_0_0_1px_rgba(139,92,246,0.4)] text-xs sm:text-sm font-semibold text-violet-800 dark:text-violet-200 transition-colors whitespace-nowrap'
      : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card hover:border-violet-400/60 hover:bg-violet-500/10 dark:hover:border-violet-400/40 text-xs sm:text-sm font-medium text-foreground transition-colors whitespace-nowrap';
  };

  return (
    <LearnPage
      title="DiviDrip Learn — Dividend Investing Guides, ETF Comparisons, DRIP Explainers"
      description="Free guides for dividend investors. DRIP explained, dividend tiers, JEPI vs JEPQ, SCHD vs VOO, covered call ETFs, MLPs, international dividends, and more."
      canonical="https://twylightcrow.com/learn"
      kicker="DiviDrip · Learn"
      h1="Dividend Investing Guides"
      wide
    >
      <p>
        Free guides, written for dividend investors — not finance majors.
      </p>

      {/* Round 92i — fuzzy search filter. Empty = show all. Searches
          title + blurb + tag substring (case-insensitive). */}
      <div className="not-prose mt-6 relative" data-testid="learn-search-wrap">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${totalAll} guides — try "yield", "REIT", "tax"...`}
          className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-400/60 transition-colors"
          data-testid="learn-search-input"
          aria-label="Search learn guides"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground"
            aria-label="Clear search"
            data-testid="learn-search-clear"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        {query && (
          <p className="mt-1.5 text-xs text-muted-foreground" data-testid="learn-search-count">
            {totalMatched === 0
              ? `No guides match "${query}". Try a different term.`
              : `${totalMatched} ${totalMatched === 1 ? 'guide' : 'guides'} match "${query}"`}
          </p>
        )}
      </div>

      {/* Section quick-nav — sticky strip. Mobile & tablet: horizontal
          scroll at top. Desktop (lg+): hidden — replaced by the
          vertical left sidebar below so all sections stay visible as
          the reader scrolls. Round 96t (Jul 4 2026). */}
      <nav
        aria-label="Section navigation (mobile)"
        className="not-prose lg:hidden sticky top-0 z-30 -mx-4 sm:mx-0 mt-6 mb-2 bg-background/90 backdrop-blur-md border-y border-border sm:border sm:rounded-xl"
        data-testid="learn-section-nav"
      >
        <ul className="flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible px-3 py-2 hide-scrollbar">
          {SECTIONS.map((s) => (
            <li key={s.id} className="shrink-0">
              <a
                href={`#${s.id}`}
                data-testid={`learn-nav-chip-${s.id}`}
                data-active={activeSection === s.id ? 'true' : undefined}
                className={chipClass(s.id, 'violet')}
              >
                <span aria-hidden="true">{s.icon}</span>
                <span>{s.short}</span>
                <span className="text-muted-foreground">({s.posts.length})</span>
              </a>
            </li>
          ))}
          {/* Round 97l (Feb 2026) — Divider + Splits & Corporate
              Actions jump pill + individual shared-section chips,
              inserted between the dividend and non-dividend blocks
              so the mobile scroller mirrors the desktop sidebar flow
              (Vehicles → Splits & Taxes → Non-Div Basics). */}
          {SHARED_SECTIONS.length > 0 && (
            <>
              <li className="shrink-0 flex items-center px-1" aria-hidden="true">
                <span className="h-5 w-px bg-border" />
              </li>
              <li className="shrink-0">
                <a
                  href="#splits-and-corporate-actions"
                  data-testid="learn-nav-chip-splits-jump"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-xs sm:text-sm font-semibold text-sky-700 dark:text-sky-300 transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">⚖️</span>
                  <span>Dividend &amp; Non-Dividend</span>
                </a>
              </li>
              {SHARED_SECTIONS.map((s) => (
                <li key={s.id} className="shrink-0">
                  <a
                    href={`#${s.id}`}
                    data-testid={`learn-nav-chip-${s.id}`}
                    data-active={activeSection === s.id ? 'true' : undefined}
                    className={chipClass(s.id, 'sky')}
                  >
                    <span aria-hidden="true">{s.icon}</span>
                    <span>{s.short}</span>
                    <span className="text-muted-foreground">({s.posts.length})</span>
                  </a>
                </li>
              ))}
            </>
          )}
          {/* Round 97j (Feb 28, 2026) — Divider + Non-Dividend section
              chips appended to the same sticky nav so users can jump
              straight from the top of the page to any non-div section. */}
          {NON_DIV_SECTIONS.length > 0 && (
            <>
              <li className="shrink-0 flex items-center px-1" aria-hidden="true">
                <span className="h-5 w-px bg-border" />
              </li>
              <li className="shrink-0">
                <a
                  href="#non-dividend-guides"
                  data-testid="learn-nav-chip-non-dividend-jump"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-xs sm:text-sm font-semibold text-amber-700 dark:text-amber-300 transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">⬇</span>
                  <span>Non-Dividend Guides</span>
                </a>
              </li>
              {NON_DIV_SECTIONS.map((s) => (
                <li key={s.id} className="shrink-0">
                  <a
                    href={`#${s.id}`}
                    data-testid={`learn-nav-chip-${s.id}`}
                    data-active={activeSection === s.id ? 'true' : undefined}
                    className={chipClass(s.id, 'amber')}
                  >
                    <span aria-hidden="true">{s.icon}</span>
                    <span>{s.short}</span>
                    <span className="text-muted-foreground">({s.posts.length})</span>
                  </a>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>

      {/* Round 96t (Jul 4 2026) — Desktop-only two-column layout:
          Left column = vertical sticky section-nav sidebar showing
          all sections (dividend + non-dividend) so the reader can
          see the full map of guides at any scroll depth. Right
          column = the existing content flow. Below lg, this
          collapses to a single column and the horizontal sticky
          strip above takes over. The sidebar is sticky (not fixed)
          so it stays inside the container's column and never
          overlaps the H1 or footer. */}
      <div className="not-prose lg:flex lg:gap-8 lg:items-start mt-8">
        <aside
          aria-label="Section navigation (desktop)"
          className="hidden lg:block lg:sticky lg:top-44 lg:self-start lg:w-56 lg:shrink-0 lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto pr-2 hide-scrollbar"
          data-testid="learn-section-sidebar"
        >
          <LearnSidebar mode="index" activeSectionId={activeSection} />
        </aside>

        <div className="lg:flex-1 lg:min-w-0 space-y-10" data-testid="learn-dividend-guides-wrap">
        {/* Round 97j (Feb 28, 2026) — Section grouping. The existing
            43 guides cover dividend-paying stocks; a separate group
            for non-dividend guides sits below. Both groups use the
            same violet→amber gradient title treatment as the page
            H1 but at a smaller scale so it reads as a sub-section. */}
        <h2
          className="text-xl sm:text-2xl font-extrabold bg-gradient-to-br from-violet-600 via-violet-500 to-amber-600 bg-clip-text text-transparent pb-1"
          data-testid="learn-dividend-guides-title"
        >
          Dividend Stock Guides
        </h2>
        {filteredSections.map((section, idx) => {
          // Round 96r (Jul 4 2026) — "Back to top" link at the end of
          // every section, matching Glossary's UX. Suppress on the
          // very last section on the page — the site footer already
          // has a back-to-top there, and doubling up looks noisy.
          const isLastOnPage =
            idx === filteredSections.length - 1 && filteredNonDivSections.length === 0;
          return (
          <section
            key={section.title}
            id={section.id}
            data-testid={`learn-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="scroll-mt-24"
          >
            <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground mb-4 pb-2 border-b border-border/60">
              <span aria-hidden="true" className="text-2xl leading-none">
                {section.icon}
              </span>
              <span>{section.title}</span>
              <span className="ml-1 text-xs font-medium text-muted-foreground">
                ({section.posts.length})
              </span>
            </h2>

            <ul className="space-y-3">
              {section.posts.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    className="block p-4 rounded-lg border border-border hover:border-violet-400/60 dark:hover:border-violet-400/40 bg-card hover:bg-card/80 transition-colors group"
                    data-testid={`learn-card-${p.to.split('/').pop()}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.3em] text-violet-500/85 dark:text-violet-300/70 font-semibold mb-1">
                      {p.tag}
                    </p>
                    <h3 className="text-lg font-bold text-foreground group-hover:text-violet-600 dark:group-hover:text-violet-300">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-foreground/75">{p.blurb}</p>
                    <p className="mt-2 text-xs text-violet-600 dark:text-violet-400 group-hover:text-amber-600 dark:group-hover:text-amber-400">
                      Read more →
                    </p>
                  </Link>
                </li>
              ))}
            </ul>

            {!isLastOnPage && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
                  data-testid={`learn-back-to-top-${section.id}`}
                >
                  <ArrowUp className="w-3 h-3" />
                  Back to top
                </button>
              </div>
            )}
          </section>
          );
        })}
      {/* Aug 2026 — SHARED guides (splits, corporate actions) — apply
          to BOTH dividend and non-dividend investors, so they render
          between the two silos visually. */}
      {filteredSharedSections.length > 0 && (
        <div
          id="splits-and-corporate-actions"
          className="mt-16 space-y-10 scroll-mt-24"
          data-testid="learn-shared-guides-wrap"
        >
          <h2
            className="text-xl sm:text-2xl font-extrabold bg-gradient-to-br from-violet-600 via-sky-500 to-amber-600 bg-clip-text text-transparent pb-1"
            data-testid="learn-shared-guides-title"
          >
            Dividend &amp; Non-Dividend <span className="text-xs font-medium text-muted-foreground normal-case">(applies to every stock)</span>
          </h2>
          {filteredSharedSections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              data-testid={`learn-section-${section.id}`}
              className="scroll-mt-24"
            >
              <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground mb-4 pb-2 border-b border-border/60">
                <span aria-hidden="true" className="text-2xl leading-none">{section.icon}</span>
                <span>{section.title}</span>
                <span className="ml-1 text-xs font-medium text-muted-foreground">({section.posts.length})</span>
              </h3>
              <ul className="space-y-3">
                {section.posts.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      className="block p-4 rounded-lg border border-border hover:border-sky-400/60 dark:hover:border-sky-400/40 bg-card hover:bg-card/80 transition-colors group"
                      data-testid={`learn-card-${p.to.split('/').pop()}`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] text-sky-500/85 dark:text-sky-300/70 font-semibold mb-1">{p.tag}</p>
                      <h4 className="text-lg font-bold text-foreground group-hover:text-sky-600 dark:group-hover:text-sky-300">{p.title}</h4>
                      <p className="mt-1.5 text-sm text-foreground/75">{p.blurb}</p>
                      <p className="mt-2 text-xs text-sky-600 dark:text-sky-400 group-hover:text-amber-600 dark:group-hover:text-amber-400">Read more →</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {/* Round 97j (Feb 28, 2026) — Non-Dividend Stock Guides box.
          Placeholder scaffold ready to receive the upcoming non-div
          guide categories + posts (free to read, no paywall). Hidden
          when NON_DIV_SECTIONS is empty so it doesn't render an empty
          stub on production. */}
      {filteredNonDivSections.length > 0 && (
        <div
          id="non-dividend-guides"
          className="mt-16 space-y-10 scroll-mt-24"
          data-testid="learn-non-dividend-guides-wrap"
        >
          <h2
            className="text-xl sm:text-2xl font-extrabold bg-gradient-to-br from-violet-600 via-violet-500 to-amber-600 bg-clip-text text-transparent pb-1"
            data-testid="learn-non-dividend-guides-title"
          >
            Non-Dividend Stock Guides
          </h2>
          {filteredNonDivSections.map((section, idx) => {
            const isLastOnPage = idx === filteredNonDivSections.length - 1;
            return (
            <section
              key={section.title}
              id={section.id}
              data-testid={`learn-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="scroll-mt-24"
            >
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground mb-4 pb-2 border-b border-border/60">
                <span aria-hidden="true" className="text-2xl leading-none">
                  {section.icon}
                </span>
                <span>{section.title}</span>
                <span className="ml-1 text-xs font-medium text-muted-foreground">
                  ({section.posts.length})
                </span>
              </h2>

              <ul className="space-y-3">
                {section.posts.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      className="block p-4 rounded-lg border border-border hover:border-violet-400/60 dark:hover:border-violet-400/40 bg-card hover:bg-card/80 transition-colors group"
                      data-testid={`learn-card-${p.to.split('/').pop()}`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] text-violet-500/85 dark:text-violet-300/70 font-semibold mb-1">
                        {p.tag}
                      </p>
                      <h3 className="text-lg font-bold text-foreground group-hover:text-violet-600 dark:group-hover:text-violet-300">
                        {p.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-foreground/75">{p.blurb}</p>
                      <p className="mt-2 text-xs text-violet-600 dark:text-violet-400 group-hover:text-amber-600 dark:group-hover:text-amber-400">
                        Read more →
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>

              {!isLastOnPage && (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
                    data-testid={`learn-back-to-top-${section.id}`}
                  >
                    <ArrowUp className="w-3 h-3" />
                    Back to top
                  </button>
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}
        </div>
      </div>
    </LearnPage>
  );
}
